import {
	AgGridEvent,
	ColDef,
	ColumnApi,
	FirstDataRenderedEvent,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-balham.min.css";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useComponentTypesContext } from "@/pages/configurations/componentTypes/context/ComponentTypeContext";
import { PartComponentType } from "@/pages/configurations/partComponentTypes/models/PartComponentType";
import { Grid, TextField } from "@material-ui/core";
import { isNil } from "lodash-es";

type Props = {
	partComponentTypes: { [ctypeId: number]: PartComponentType };
	onSelectionChanged: Function;
};

export const DataTable = ({
	partComponentTypes,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);

	const {
		state: { componentTypes },
	} = useComponentTypesContext();

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);
	};

	const sortRows = (event: AgGridEvent) => {
		event.columnApi.applyColumnState({
			state: [{ colId: "componentTypeName", sort: "asc", sortIndex: 0 }],
		});
	};

	const rows = useMemo(() => {
		return componentTypes
			.filter((ctType) => !ctType.isRepair)
			.map(({ componentTypeId, componentTypeName, rootTypeId }) => {
				return {
					componentTypeId,
					componentTypeName,
					rootTypeId,
				};
			});
	}, [componentTypes]);

	const columns: ColDef[] = [
		{
			field: "componentTypeId",
			headerName: "  ",
			flex: 1,
			maxWidth: 100,
			checkboxSelection: true,
			showDisabledCheckboxes: true,
			sortIndex: 1,
			filter: "filterIsRepair",
		},
		{
			field: "componentTypeName",
			headerName: "Name",
			flex: 4,
			sortIndex: 0,

			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.componentTypeName?.localeCompare(
						nodeB.data.componentTypeName,
					);
				}
			},
		},
	];

	const defaultColDef = useMemo(
		() => ({
			resizable: true,
			sortable: true,
			filter: true,
		}),
		[],
	);

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows(event);
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(
		(params: FirstDataRenderedEvent) => {
			params.api.forEachNode((node) => {
				node.setSelected(
					!!node.data && !isNil(partComponentTypes[node.data.componentTypeId]),
				);
			});
			sortRows(params);
		},
		[sortRows],
	);

	const onGridReady = useCallback(
		(event: GridReadyEvent) => {
			setGridApi(event.api);
			setColumnApi(event.columnApi);
		},
		[setGridApi],
	);

	useEffect(() => {
		gridApi?.setQuickFilter(filterInput);
	}, [filterInput, gridApi]);

	return (
		<>
			<Grid md={12} xs={12} style={{ width: "100rem", padding: "10px 0px" }}>
				<TextField
					id="outlined-basic"
					label="Filter..."
					variant="outlined"
					style={{ width: "100%" }}
					value={filterInput}
					onChange={handlerFilter}
				/>
			</Grid>

			<div
				className="ag-theme-balham"
				style={{ width: "100%", height: "60vh" }}
			>
				<AgGridReact
					getRowId={(params) => params.data.componentTypeId}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					rowSelection={"multiple"}
					rowMultiSelectWithClick
					onSelectionChanged={onAgGridSelectionChanged}
				/>
			</div>
		</>
	);
};
